/**
 * Model calculation variables panel — Angular `model-add` / `model-edit` parity.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, LayoutGrid, Maximize2, Search, Users } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import type {
  ModelCalculationParamSelection,
  ModelParamDefinition,
  ModelParamGroup,
  ModelParamUiSection,
} from '../types/modelParam'
import { getModelParamUiSections } from '../utils/modelParamMapping'
import styles from '../styles/modelAdd.module.css'

interface ModelCalculationParamsPanelProps {
  definitions: ModelParamDefinition[]
  value: ModelCalculationParamSelection
  onChange: (next: ModelCalculationParamSelection) => void
}

function getGroupFieldKeys(group: ModelParamGroup): string[] {
  return group.fields.map((field) => field.key)
}

function getSectionFieldKeys(section: ModelParamUiSection): string[] {
  return section.groups.reduce<string[]>((keys, group) => {
    group.fields.forEach((field) => keys.push(field.key))
    return keys
  }, [])
}

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  indeterminate: boolean
  onChange: (checked: boolean) => void
  'aria-label'?: string
}) {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.checked)}
    />
  )
}

export function ModelCalculationParamsPanel({
  definitions,
  value,
  onChange,
}: ModelCalculationParamsPanelProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandAll, setExpandAll] = useState(false)
  const [sectionState, setSectionState] = useState<Record<string, boolean>>({})
  const [groupState, setGroupState] = useState<Record<string, boolean>>({})

  const visibleSections = useMemo(
    () => getModelParamUiSections(definitions, searchTerm),
    [definitions, searchTerm],
  )

  const getGroupStateKey = (section: ModelParamUiSection, group: ModelParamGroup) =>
    `${section.id}::${group.title}`

  const isSectionOpen = useCallback(
    (section: ModelParamUiSection) => {
      if (searchTerm) return true
      if (expandAll) return true
      return !!sectionState[section.id]
    },
    [expandAll, searchTerm, sectionState],
  )

  const isGroupOpen = useCallback(
    (section: ModelParamUiSection, group: ModelParamGroup) => {
      if (searchTerm) return true
      if (expandAll) return true
      return !!groupState[getGroupStateKey(section, group)]
    },
    [expandAll, getGroupStateKey, groupState, searchTerm],
  )

  const areAllSectionFieldsSelected = (section: ModelParamUiSection) => {
    const keys = getSectionFieldKeys(section)
    return keys.length > 0 && keys.every((key) => !!value[key])
  }

  const isSectionPartiallySelected = (section: ModelParamUiSection) => {
    const keys = getSectionFieldKeys(section)
    const selectedCount = keys.filter((key) => !!value[key]).length
    return selectedCount > 0 && selectedCount < keys.length
  }

  const areAllGroupFieldsSelected = (group: ModelParamGroup) => {
    const keys = getGroupFieldKeys(group)
    return keys.length > 0 && keys.every((key) => !!value[key])
  }

  const isGroupPartiallySelected = (group: ModelParamGroup) => {
    const keys = getGroupFieldKeys(group)
    const selectedCount = keys.filter((key) => !!value[key]).length
    return selectedCount > 0 && selectedCount < keys.length
  }

  const setKeys = (keys: string[], selected: boolean) => {
    const next = { ...value }
    keys.forEach((key) => {
      next[key] = selected
    })
    onChange(next)
  }

  const toggleSection = (section: ModelParamUiSection) => {
    setSectionState((prev) => ({ ...prev, [section.id]: !isSectionOpen(section) }))
  }

  const toggleGroup = (section: ModelParamUiSection, group: ModelParamGroup) => {
    const key = getGroupStateKey(section, group)
    setGroupState((prev) => ({ ...prev, [key]: !isGroupOpen(section, group) }))
  }

  const areAllVisibleSectionsOpen = visibleSections.length > 0 &&
    visibleSections.every((section) => isSectionOpen(section))

  const toggleAllSections = () => {
    const shouldExpand = !areAllVisibleSectionsOpen
    setExpandAll(shouldExpand)
    const nextSectionState: Record<string, boolean> = {}
    const nextGroupState: Record<string, boolean> = {}
    visibleSections.forEach((section) => {
      nextSectionState[section.id] = shouldExpand
      section.groups.forEach((group) => {
        nextGroupState[getGroupStateKey(section, group)] = shouldExpand
      })
    })
    setSectionState(nextSectionState)
    setGroupState(nextGroupState)
  }

  const renderParamGrid = (group: ModelParamGroup) => (
    <div className={styles.modelParamGrid}>
      {group.fields.map((field) => (
        <label key={field.key} className={styles.modelParamCard}>
          <input
            type="checkbox"
            checked={!!value[field.key]}
            onChange={(event) => {
              onChange({ ...value, [field.key]: event.target.checked })
            }}
          />
          <span className={styles.modelParamCardContent}>
            <span className={styles.modelParamCardName}>{field.key}</span>
            <small className={styles.modelParamCardDesc}>{field.description}</small>
          </span>
        </label>
      ))}
    </div>
  )

  return (
    <div className={styles.modelParamPanel}>
      <div className={styles.modelParamToolbar}>
        <div className={styles.modelParamHeading}>
          <span className={styles.modelParamHeadingIcon}>
            <LayoutGrid size={18} aria-hidden />
          </span>
          <span>
            <span className={styles.modelParamHeadingTitle}>Model Variables</span>
            <span className={styles.modelParamHeadingSubtitle}>
              Manage and configure variables used for model calculation
            </span>
          </span>
        </div>

        <div className={styles.modelParamActions}>
          <div className={styles.modelParamSearch}>
            <Search size={14} aria-hidden />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search variables..."
            />
          </div>
          <button type="button" className={styles.modelParamExpandBtn} onClick={toggleAllSections}>
            <Maximize2 size={14} aria-hidden />
            {areAllVisibleSectionsOpen ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      </div>

      {visibleSections.length === 0 ? (
        <div className={styles.modelParamEmpty}>No variables match your search.</div>
      ) : (
        visibleSections.map((section) => (
          <div
            key={section.id}
            className={cn(styles.modelParamSection, isSectionOpen(section) && styles.modelParamSectionOpen)}
          >
            <button
              type="button"
              className={styles.modelParamSectionSummary}
              onClick={() => toggleSection(section)}
            >
              <span className={styles.modelParamSectionTitleWrap}>
                <span
                  className={cn(
                    styles.modelParamSectionIcon,
                    section.iconTone === 'purple'
                      ? styles.modelParamSectionIconPurple
                      : styles.modelParamSectionIconBlue,
                  )}
                >
                  {section.scope === 'applicant' ? (
                    <Users size={16} aria-hidden />
                  ) : (
                    <LayoutGrid size={16} aria-hidden />
                  )}
                </span>
                <span>
                  <span className={styles.modelParamSectionTitle}>{section.title}</span>
                  <span className={styles.modelParamCount}>{section.totalFields} variables</span>
                </span>
              </span>
              <ChevronDown size={14} className={styles.modelParamSectionChevron} aria-hidden />
            </button>

            {isSectionOpen(section) ? (
              <div className={styles.modelParamSectionBody}>
                {section.scope !== 'applicant' ? (
                  <label className={styles.modelParamSelectAll}>
                    <IndeterminateCheckbox
                      checked={areAllSectionFieldsSelected(section)}
                      indeterminate={isSectionPartiallySelected(section)}
                      aria-label={`Select all ${section.title}`}
                      onChange={(checked) => setKeys(getSectionFieldKeys(section), checked)}
                    />
                    <span>Select all</span>
                  </label>
                ) : null}

                {section.scope === 'applicant'
                  ? section.groups.map((group) => (
                      <div
                        key={group.title}
                        className={cn(
                          styles.modelParamSubsection,
                          isGroupOpen(section, group) && styles.modelParamSubsectionOpen,
                        )}
                      >
                        <button
                          type="button"
                          className={styles.modelParamSubsectionSummary}
                          onClick={() => toggleGroup(section, group)}
                        >
                          <span>{group.title}</span>
                          <span className={styles.modelParamCount}>{group.fields.length} variables</span>
                          <ChevronDown size={12} className={styles.modelParamSubsectionChevron} aria-hidden />
                        </button>

                        {isGroupOpen(section, group) ? (
                          <div className={styles.modelParamSubsectionBody}>
                            <label className={styles.modelParamSelectAll}>
                              <IndeterminateCheckbox
                                checked={areAllGroupFieldsSelected(group)}
                                indeterminate={isGroupPartiallySelected(group)}
                                aria-label={`Select all ${group.title}`}
                                onChange={(checked) => setKeys(getGroupFieldKeys(group), checked)}
                              />
                              <span>Select all</span>
                            </label>
                            {renderParamGrid(group)}
                          </div>
                        ) : null}
                      </div>
                    ))
                  : section.groups.map((group) => (
                      <div key={group.title}>{renderParamGrid(group)}</div>
                    ))}
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  )
}
